/**
 * Ground-state electron configurations of the neutral atoms, hydrogen to oganesson.
 *
 * ## Source
 *
 * Z = 1–108 are the configurations in NIST's "Ground Levels and Ionization Energies for the
 * Neutral Atoms" (W. C. Martin, A. Musgrove, S. Kotochigova and J. E. Sansonetti, NIST
 * Atomic Spectra Database), including every exception to the Madelung (Aufbau) filling
 * order: Cr, Cu, Nb, Mo, Ru, Rh, Pd, Ag, La, Ce, Gd, Pt, Au, Ac, Th, Pa, U, Np, Cm and Lr.
 * NIST abbreviates the cores of In–Xe as [Cd] and of Tl–Rn as [Hg]; they are written here
 * with the noble-gas core, so [Cd]5p¹ becomes [Kr]4d¹⁰ 5s² 5p¹.
 *
 * Z = 109–118 are not in NIST's table (only a few atoms of each have ever been made), so
 * they follow the Madelung order and are marked predicted. Relativistic calculations suggest
 * that some of them, roentgenium among them, may differ.
 *
 * Each entry is `[core]` followed by `<n><s|p|d|f><electrons>` groups.
 */

/** Index = atomic number − 1. */
// biome-ignore format: one row per element, compact
export const GROUND_STATES: readonly string[] = [
  "1s1", "1s2",
  "[He]2s1", "[He]2s2", "[He]2s2 2p1", "[He]2s2 2p2", "[He]2s2 2p3", "[He]2s2 2p4", "[He]2s2 2p5", "[He]2s2 2p6",
  "[Ne]3s1", "[Ne]3s2", "[Ne]3s2 3p1", "[Ne]3s2 3p2", "[Ne]3s2 3p3", "[Ne]3s2 3p4", "[Ne]3s2 3p5", "[Ne]3s2 3p6",
  "[Ar]4s1", "[Ar]4s2",
  "[Ar]3d1 4s2", "[Ar]3d2 4s2", "[Ar]3d3 4s2", "[Ar]3d5 4s1", "[Ar]3d5 4s2",
  "[Ar]3d6 4s2", "[Ar]3d7 4s2", "[Ar]3d8 4s2", "[Ar]3d10 4s1", "[Ar]3d10 4s2",
  "[Ar]3d10 4s2 4p1", "[Ar]3d10 4s2 4p2", "[Ar]3d10 4s2 4p3", "[Ar]3d10 4s2 4p4", "[Ar]3d10 4s2 4p5", "[Ar]3d10 4s2 4p6",
  "[Kr]5s1", "[Kr]5s2",
  "[Kr]4d1 5s2", "[Kr]4d2 5s2", "[Kr]4d4 5s1", "[Kr]4d5 5s1", "[Kr]4d5 5s2",
  "[Kr]4d7 5s1", "[Kr]4d8 5s1", "[Kr]4d10", "[Kr]4d10 5s1", "[Kr]4d10 5s2",
  "[Kr]4d10 5s2 5p1", "[Kr]4d10 5s2 5p2", "[Kr]4d10 5s2 5p3", "[Kr]4d10 5s2 5p4", "[Kr]4d10 5s2 5p5", "[Kr]4d10 5s2 5p6",
  "[Xe]6s1", "[Xe]6s2",
  "[Xe]5d1 6s2", "[Xe]4f1 5d1 6s2", "[Xe]4f3 6s2", "[Xe]4f4 6s2", "[Xe]4f5 6s2", "[Xe]4f6 6s2", "[Xe]4f7 6s2",
  "[Xe]4f7 5d1 6s2", "[Xe]4f9 6s2", "[Xe]4f10 6s2", "[Xe]4f11 6s2", "[Xe]4f12 6s2", "[Xe]4f13 6s2", "[Xe]4f14 6s2",
  "[Xe]4f14 5d1 6s2", "[Xe]4f14 5d2 6s2", "[Xe]4f14 5d3 6s2", "[Xe]4f14 5d4 6s2", "[Xe]4f14 5d5 6s2",
  "[Xe]4f14 5d6 6s2", "[Xe]4f14 5d7 6s2", "[Xe]4f14 5d9 6s1", "[Xe]4f14 5d10 6s1", "[Xe]4f14 5d10 6s2",
  "[Xe]4f14 5d10 6s2 6p1", "[Xe]4f14 5d10 6s2 6p2", "[Xe]4f14 5d10 6s2 6p3", "[Xe]4f14 5d10 6s2 6p4",
  "[Xe]4f14 5d10 6s2 6p5", "[Xe]4f14 5d10 6s2 6p6",
  "[Rn]7s1", "[Rn]7s2",
  "[Rn]6d1 7s2", "[Rn]6d2 7s2", "[Rn]5f2 6d1 7s2", "[Rn]5f3 6d1 7s2", "[Rn]5f4 6d1 7s2", "[Rn]5f6 7s2", "[Rn]5f7 7s2",
  "[Rn]5f7 6d1 7s2", "[Rn]5f9 7s2", "[Rn]5f10 7s2", "[Rn]5f11 7s2", "[Rn]5f12 7s2", "[Rn]5f13 7s2", "[Rn]5f14 7s2",
  "[Rn]5f14 7s2 7p1", "[Rn]5f14 6d2 7s2", "[Rn]5f14 6d3 7s2", "[Rn]5f14 6d4 7s2", "[Rn]5f14 6d5 7s2", "[Rn]5f14 6d6 7s2",
  "[Rn]5f14 6d7 7s2", "[Rn]5f14 6d8 7s2", "[Rn]5f14 6d9 7s2", "[Rn]5f14 6d10 7s2",
  "[Rn]5f14 6d10 7s2 7p1", "[Rn]5f14 6d10 7s2 7p2", "[Rn]5f14 6d10 7s2 7p3", "[Rn]5f14 6d10 7s2 7p4",
  "[Rn]5f14 6d10 7s2 7p5", "[Rn]5f14 6d10 7s2 7p6",
];

/** The first element whose configuration is predicted rather than measured (meitnerium). */
export const FIRST_PREDICTED_Z = 109;

/** Noble gases and their atomic numbers, for [core] notation. */
export const NOBLE_GASES: readonly { symbol: string; z: number }[] = [
  { symbol: "He", z: 2 },
  { symbol: "Ne", z: 10 },
  { symbol: "Ar", z: 18 },
  { symbol: "Kr", z: 36 },
  { symbol: "Xe", z: 54 },
  { symbol: "Rn", z: 86 },
];

export const SUBSHELL_LETTERS = ["s", "p", "d", "f"] as const;
